import { useState } from "react";
import { createPortal } from "react-dom";

export interface FlatArtSignature {
  text: string;
  /** CSS font family, e.g. "Great Vibes". */
  fontFamily: string;
  /** Size multiplier on the auto-computed base. 1.0 = auto. */
  scale?: number;
}

interface Props {
  /** Watermarked AI portrait URL (server-side watermarked — safe to show). */
  src: string;
  /** Optional customer signature, overlaid bottom-right. */
  signature?: FlatArtSignature | null;
}

/** Block context-menu + drag so the watermarked preview isn't trivially
 *  saved. A deterrent, not DRM — the real protection is that the clean,
 *  un-watermarked master never reaches the browser (see api/lib/watermark). */
function guard(e: React.SyntheticEvent) {
  e.preventDefault();
}

/**
 * Flat reveal of the AI portrait — a single clean image, with no mockup
 * compositing or 3D canvas-wrap edges. Renders as a plain <img> (not a
 * <canvas>), so it shows on any origin without a CORS dependency. The
 * optional signature is a CSS overlay, sized to the image via container
 * query units so it scales with whatever width the image renders at.
 * Click to zoom.
 */
export function FlatArtPreview({ src, signature }: Props) {
  const [zoomed, setZoomed] = useState(false);
  const sig = signature && signature.text.trim() ? signature : null;

  const inner = (
    <>
      <img
        className="flat-art__img"
        src={src}
        alt="Your AI portrait preview"
        draggable={false}
        onContextMenu={guard}
        onDragStart={guard}
      />
      {sig && (
        <span
          className="flat-art__sig"
          style={
            {
              fontFamily: `"${sig.fontFamily}", cursive`,
              "--sig-scale": sig.scale ?? 1,
            } as React.CSSProperties
          }
        >
          {sig.text}
        </span>
      )}
    </>
  );

  return (
    <>
      {/* key={String(zoomed)} remounts this figure on every zoom open/close.
          The lightbox uses backdrop-filter, which makes the browser snapshot
          everything behind it into GPU compositor layers; on close this
          persistent <img> can be left painting stale tiles (the garbled
          rainbow-mosaic bug). A remounted element gets a fresh layer and
          always repaints cleanly. */}
      <figure
        key={String(zoomed)}
        className="flat-art"
        onClick={() => setZoomed(true)}
        title="Click to enlarge"
      >
        {inner}
      </figure>

      {zoomed &&
        createPortal(
          <div className="mockup-lightbox" onClick={() => setZoomed(false)}>
            <figure className="flat-art flat-art--zoom" onClick={(e) => e.stopPropagation()}>
              {inner}
            </figure>
            <button
              className="mockup-lightbox__close"
              onClick={() => setZoomed(false)}
              aria-label="Close zoom"
            >
              ×
            </button>
          </div>,
          document.body,
        )}
    </>
  );
}
